import { cn } from "@/lib/utils";
import { XCircleIcon } from "lucide-react";
import Image from "next/image";
import type { ReactEventHandler } from "react";

export const SingleImageInput = ({
	name,
	image,
	size,
	onDelete,
	onLoad,
	preload,
	isUploading,
	width = 300,
	height = 300,
}: {
	name?: string;
	image: string;
	size?: "large" | "small";
	onDelete?: () => void;
	onLoad?: ReactEventHandler<HTMLImageElement>;
	preload?: string;
	isUploading?: boolean;
	aspectRatio?: string;
	width?: number;
	height?: number;
}) => {
	const aspectRatio = width && height ? `${width} / ${height}` : `1`;
	const imageWidth = size === "large" ? width : width / 3 - 16;
	const imageHeight = size === "large" ? height : height / 3 - 16;

	return (
		<div className="group relative w-full rounded-md object-cover" style={{ aspectRatio }}>
			<input type="hidden" name={name} value={preload || image} />
			{preload && (
				<Image
					alt={name ?? ""}
					className="pointer-events-none absolute opacity-0"
					width={imageWidth}
					height={imageHeight}
					src={preload}
					onLoad={onLoad}
					draggable="false"
				/>
			)}

			<Image
				alt={name ?? ""}
				className="w-full object-cover h-full rounded-md"
				width={imageWidth}
				height={imageHeight}
				src={image}
				onLoad={onLoad}
				draggable="false"
			/>
			{isUploading && (
				<div
					aria-busy
					className="absolute bottom-0 h-1 w-full animate-[moving-gradient] bg-linear-to-r from-blue-400 via-blue-800 to-blue-400 shadow-xs duration-1000 [animation-iteration-count:infinite] [background-size:50%_100%]"
				/>
			)}
			{onDelete && (
				<button
					type="button"
					className={cn(
						"absolute right-0 z-20 top-0 p-1 opacity-70 transition-opacity hover:opacity-100",
						size === "large" && "p-1.5",
					)}
					onClickCapture={(e) => {
						e.preventDefault();
						onDelete();
					}}
					title="Delete image"
				>
					<XCircleIcon className="h-4 w-4 fill-white" />
					<span className="sr-only">Delete image</span>
				</button>
			)}
		</div>
	);
};
